import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { ErrorState } from './ErrorState';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Stops one broken component taking the whole page down.
 *
 * Still a class: React has no hook equivalent of componentDidCatch.
 *
 * Note this catches *render* errors only - not failed requests, which surface
 * through React Query, and not errors inside event handlers.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Replaced by Sentry once it is wired up.
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <ErrorState
        title="Something went wrong"
        description="This part of the page failed to load. Reloading usually fixes it."
        action={
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload the page
          </Button>
        }
      />
    );
  }
}
